<script setup lang="ts">
import { SaleSteppedPrice } from '@lib/shared';
import type { PropType } from 'vue';

import { type DirectSaleItemEntity, type DirectSaleNegotiationPlatformEntity, NegotiationState } from '~/apiClient';

// PROPS
const { directSale, negotiation, delegateMakeProposal } = defineProps({
    directSale: { type: Object as PropType<DirectSaleItemEntity>, required: true },
    negotiation: { type: Object as PropType<DirectSaleNegotiationPlatformEntity>, required: false, default: undefined },
    delegateMakeProposal: { type: Boolean, required: false, default: false },
});

const isModalOpen = defineModel<boolean>({ required: true });

// EVENTS
const emit = defineEmits<{
    confirmProposal: [number];
}>();

// COMPOSABLES
const { tt, tn } = useTypedI18n();
const { isMobile } = useResponsive();
const { data: authData } = useAuthUtils();
const { makeProposal, makeProposalIsPending } = useDirectSaleNegotiation(() => directSale);

const getAmount = (base: number, deltaSteps: number): number => {
    const auctionSteppedPrice = new SaleSteppedPrice(
        directSale.minNegotiationStepAmount,
        directSale.minNegotiationStepAmountThreshold,
        directSale.maxNegotiationStepAmount
    );

    return auctionSteppedPrice.getDeltaStepsAmount(auctionSteppedPrice.getStepRoundedReservePrice(base), deltaSteps);
};

// DATA
const isBuyer = computed(() => directSale.sellerCompanyId !== authData.value?.user?.companyId);

const myProposal = computed(() => {
    if (!negotiation) return undefined;

    return isBuyer.value ? negotiation.currentBuyerProposal.amount : negotiation.currentSellerProposal.amount;
});

const counterPartProposal = computed(() => {
    if (!negotiation) return undefined;

    return isBuyer.value ? negotiation.currentSellerProposal.amount : negotiation.currentBuyerProposal.amount;
});

const initialProposal = computed(() => (!negotiation ? getAmount(directSale.price!, -1) : myProposal.value!));

const selectedProposal = ref(initialProposal.value);

const minProposal = computed(() => {
    if (!negotiation) return directSale.minNegotiationOffer;

    return isBuyer.value ? getAmount(negotiation.currentBuyerProposal.amount, 1) : negotiation.currentBuyerProposal.amount;
});

const maxProposal = computed(() => {
    if (!negotiation) return getAmount(directSale.price!, -1);

    return isBuyer.value ? negotiation.currentSellerProposal.amount : getAmount(negotiation.currentSellerProposal.amount, -1);
});

const multiStepButtonsData = computed(() => {
    const absAllSteps = [2, 5, 10];
    const allSteps = isBuyer.value && negotiation?.currentBuyerProposal.amount ? absAllSteps : absAllSteps.map(val => val * -1);

    return allSteps.map(steps => {
        const amount = getAmount(selectedProposal.value, steps);
        const delta = amount - selectedProposal.value;
        const labelSignPrefix = delta > 0 ? '+' : '';

        return {
            key: `steps_${steps}`,
            label: `${labelSignPrefix}${tn(delta, 'currency')}`,
            amount,
            hidden: amount <= minProposal.value || amount >= maxProposal.value,
        };
    });
});

const confirmButtonDisabled = computed(() => selectedProposal.value > maxProposal.value || selectedProposal.value < minProposal.value);

const resetProposalDisabled = computed(() => selectedProposal.value === initialProposal.value);

const title = computed(() => {
    if (!negotiation) return tt('directSaleNegotiation.proposalModal.firstOfferTitle');

    return tt(isBuyer.value ? 'directSaleNegotiation.proposalModal.buyerTitle' : 'directSaleNegotiation.proposalModal.sellerTitle');
});

const subtitle = computed(() => {
    if (!negotiation) return tt('directSaleNegotiation.proposalModal.firstOfferSubtitle');

    return tt(isBuyer.value ? 'directSaleNegotiation.proposalModal.buyerSubtitle' : 'directSaleNegotiation.proposalModal.sellerSubtitle');
});

const confirmButtonLabel = computed(() => {
    if (!negotiation) return tt('negotiation.makeOffer');

    return tt(isBuyer.value ? 'negotiation.updateOffer' : 'negotiation.updateRequest');
});

// METHODS
const closeModal = (): void => {
    isModalOpen.value = false;
};

const resetSelectedProposal = (): void => {
    selectedProposal.value = initialProposal.value;
};

const resetModal = (): void => {
    resetSelectedProposal();
};

const onUpdateProposal = (steps: number): void => {
    selectedProposal.value = getAmount(selectedProposal.value, steps);
};

const onConfirmProposal = async (): Promise<void> => {
    if (delegateMakeProposal) {
        emit('confirmProposal', selectedProposal.value);
        closeModal();

        return;
    }

    await makeProposal(selectedProposal.value, negotiation?.id);
    closeModal();
};

// WATCHERS
watch(
    () => negotiation?.state,
    value => isModalOpen.value && value !== NegotiationState.Open && closeModal()
);

watch(isModalOpen, (value, oldValue) => value && !oldValue && resetModal());
</script>

<template>
    <UModal
        v-model:open="isModalOpen"
        :fullscreen="isMobile"
        :ui="{ title: 'text-2xl md:text-2xl' }"
        :title="title"
        v-bind="modalNoDescAriaFixupProps"
    >
        <template #body>
            <div class="space-y-7 text-center">
                <p>{{ subtitle }}</p>
                <div class="space-y-2">
                    <div v-if="negotiation" class="grid grid-cols-2 gap-4 text-center">
                        <div v-if="myProposal" class="lg:space-y-1">
                            <p class="text-dimmed text-xs lg:text-sm">
                                {{ tt(isBuyer ? 'negotiation.yourOffer' : 'negotiation.yourRequest') }}
                            </p>
                            <p class="text-primary-500 text-2xl font-semibold">
                                {{ tn(myProposal, 'currency') }}
                            </p>
                            <SaleFees
                                :reference-date="negotiation.startAt"
                                :is-buyer="isBuyer"
                                :base-amount="myProposal"
                                :assets-count="directSale.assets.length"
                                :fee-configuration="directSale.userMeta?.feeOverride"
                            />
                        </div>
                        <div v-if="counterPartProposal" class="lg:space-y-1">
                            <p class="text-dimmed text-xs lg:text-sm">
                                {{ tt(isBuyer ? 'negotiation.sellerRequest' : 'negotiation.buyerOffer') }}
                            </p>
                            <p class="text-2xl font-semibold">
                                {{ tn(counterPartProposal, 'currency') }}
                            </p>
                            <SaleFees
                                :reference-date="negotiation.startAt"
                                :is-buyer="isBuyer"
                                :base-amount="counterPartProposal"
                                :assets-count="directSale.assets.length"
                                :fee-configuration="directSale.userMeta?.feeOverride"
                            />
                        </div>
                    </div>

                    <div v-else class="lg:space-y-1">
                        <p class="text-dimmed text-xs lg:text-sm">
                            {{ tt('directSaleNegotiation.directSalePrice') }}
                        </p>
                        <p class="text-2xl font-semibold">
                            {{ tn(directSale.price!, 'currency') }}
                        </p>
                        <SaleFees
                            v-if="directSale.price"
                            :base-amount="directSale.price"
                            :assets-count="directSale.assets.length"
                            :fee-configuration="directSale.userMeta?.feeOverride"
                            is-buyer
                        />
                    </div>
                </div>

                <div class="bg-primary-50 space-y-4 rounded-xl p-4 lg:p-6">
                    <div>
                        <p class="text-xs font-extrabold tracking-widest uppercase lg:text-sm">
                            {{ tt(isBuyer ? 'negotiation.yourOffer' : 'negotiation.yourRequest') }}
                        </p>
                        <div class="mt-3 flex items-center justify-between">
                            <UButton
                                icon="lucide:minus"
                                size="lg"
                                color="neutral"
                                variant="outline"
                                square
                                class="disabled:bg-neutral-200 disabled:text-neutral-400"
                                :disabled="selectedProposal <= minProposal"
                                @click="onUpdateProposal(-1)"
                            />
                            <span class="text-primary text-2xl lg:text-3xl">{{ tn(selectedProposal, 'currency') }}</span>
                            <UButton
                                icon="lucide:plus"
                                size="lg"
                                color="neutral"
                                variant="outline"
                                square
                                class="disabled:bg-neutral-200 disabled:text-neutral-400"
                                :disabled="selectedProposal >= maxProposal"
                                @click="onUpdateProposal(1)"
                            />
                        </div>
                        <SaleFees
                            :reference-date="negotiation?.startAt ?? new Date().toISOString()"
                            :is-buyer="isBuyer"
                            :base-amount="selectedProposal"
                            :assets-count="directSale.assets.length"
                            :fee-configuration="directSale.userMeta?.feeOverride"
                        />
                        <UButton
                            size="lg"
                            class="p-1"
                            variant="link"
                            :label="tt('directSaleNegotiation.proposalModal.resetProposal')"
                            :disabled="resetProposalDisabled"
                            @click="resetSelectedProposal"
                        />
                    </div>

                    <div class="mt-6 flex justify-center gap-3">
                        <UButton
                            v-for="buttonData in multiStepButtonsData"
                            :key="buttonData.key"
                            :class="{ invisible: buttonData.hidden }"
                            color="neutral"
                            variant="outline"
                            size="xl"
                            :label="buttonData.label"
                            @click="selectedProposal = buttonData.amount"
                        />
                    </div>
                </div>
            </div>
        </template>
        <template #footer="{ close }">
            <UButton
                size="xl"
                :label="confirmButtonLabel"
                block
                :disabled="confirmButtonDisabled"
                :loading="makeProposalIsPending"
                @click="onConfirmProposal"
            />
            <UButton size="xl" color="neutral" block variant="link" :label="tt('negotiation.cancel')" @click="close" />
        </template>
    </UModal>
</template>
